import React from 'react';
import { Box, Divider, FormControl, InputLabel, MenuItem, Select, styled } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import FlexBox from 'components/flexbox/FlexBox';
import LayoutDrawer from 'layouts/layout-parts/LayoutDrawer';
import MultiLevelMenu from './MultiLevelMenu';
import { useEffect, useState } from 'react';
import axiosInstance from 'utils/axios';
import { baseUrl } from 'utils/constants';
import { clearLocalStorage, localSet } from 'utils/utils';
import { useNavigate } from 'react-router-dom';
import FlexBetween from 'components/flexbox/FlexBetween';
import { EnvBanner } from 'utils/pieces';

interface ProgramItem {
    programGuid: string;
    programName: string;
}

interface MobileSidebarConfig {
    li_dashboard?: {
        headerlogo?: string;
        [key: string]: string | boolean | undefined;
    };
    [key: string]: Record<string, string | boolean | undefined> | undefined;
}

const TOP_HEADER_AREA = 70;
// Takes the height left under the header and scrolls when the menu is taller.
const NavWrapper = styled(Box)<{ compact?: boolean }>(() => ({
    paddingLeft: 16,
    paddingRight: 16,
    paddingBottom: 16,
    flex: 1,
    minHeight: 0,
    overflowY: `auto`,
    overscrollBehavior: `contain`,
}));

const LogoImg = styled(Box)<{ component?: React.ElementType; src?: string; alt?: string }>(() => ({
    height: `40px`,
    margin: `10px 0 0 0`,
    width: `auto`,
}));

interface MobileSidebarProps {
    sidebarCompact: boolean;
    showMobileSideBar: boolean;
    setShowMobileSideBar: () => void;
}

const MobileSidebar: React.FC<MobileSidebarProps> = props => {
    const navigate = useNavigate();
    const [config, setConfig] = useState<MobileSidebarConfig>(
        JSON.parse(localStorage.getItem(`config`) || JSON.stringify({}))
    );
    const [host] = useState<string>(window.location.host.split(`.`)[0]);
    const [allPrograms, setAllPrograms] = useState<ProgramItem[]>(
        JSON.parse(localStorage.getItem(`allPrograms`) || JSON.stringify([]))
    );
    const [selectedProgram, setSelectedProgram] = useState<string | null>(localStorage.getItem(`selectedProgram`));

    const listenerUpdate = (): void => {
        setAllPrograms(JSON.parse(localStorage.getItem(`allPrograms`) || JSON.stringify([])));
        setSelectedProgram(localStorage.getItem(`selectedProgram`));
        setConfig(JSON.parse(localStorage.getItem(`config`) || JSON.stringify({})));
    };

    const switchProgram = (v: string): Promise<void> =>
        axiosInstance
            .get(`${baseUrl}switchme?guid=${v}`)
            .then((r: { data: Record<string, string> }) => {
                Object.keys(r.data).forEach((i: string) => {
                    localSet(i, r.data[i]);
                });
                window.location.href = `/dashboard/research`;
            })
            .catch(() => {
                clearLocalStorage();
                navigate(`/login`);
            });

    useEffect(() => {
        window.addEventListener(`storage`, listenerUpdate);
        return () => window.removeEventListener(`storage`, listenerUpdate);
    }, []);

    const Separator = styled(Box)(({ theme }) => ({
        height: `40px`,
        width: `1px`,
        backgroundColor: theme.palette.divider,
        margin: `10px 45px`,
    }));

    const { sidebarCompact, showMobileSideBar, setShowMobileSideBar } = props;
    return (
        <LayoutDrawer open={showMobileSideBar} onClose={setShowMobileSideBar}>
            <Box p={2} maxHeight={TOP_HEADER_AREA} flexShrink={0}>
                <FlexBox ml={1.5}>
                    <LogoImg component="img" src="/static/logo/lilogo.svg" alt="logo" width={18} />
                    <Separator />

                    <LogoImg component="img" alt="" src={config?.li_dashboard?.headerlogo || ``} />
                </FlexBox>
            </Box>

            {!!allPrograms.length && (
                <FlexBetween pt={3} pr={2} pl={4} pb={1} height={TOP_HEADER_AREA} flexShrink={0} fullWidth>
                    <FormControl fullWidth>
                        <InputLabel id="demo-simple-select-label">Selected Program</InputLabel>
                        <Select
                            sx={{
                                '&:hover': { background: `#F7F7F7` },
                                '& div ': { padding: `7px 12px !important` },
                                minHeight: `unset !important`,
                                borderRadius: `20px !important`,
                            }}
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="Selected Program"
                            fullWidth
                            value={selectedProgram as string}
                            onChange={(v: SelectChangeEvent<string>) => switchProgram(v.target.value)}
                        >
                            {localStorage.programs}
                            {allPrograms.map(a => (
                                <MenuItem key={a.programGuid} value={a.programGuid}>
                                    {a.programName}
                                </MenuItem>
                            ))}
                            <Divider />
                            <MenuItem value="allprograms">All Programs</MenuItem>
                        </Select>
                    </FormControl>
                </FlexBetween>
            )}
            {host.startsWith(`localhost`) && <EnvBanner>LOCALHOST</EnvBanner>}
            {host.includes(`-`) && <EnvBanner>{host.split(`-`)[1].toUpperCase()}</EnvBanner>}
            <NavWrapper compact={sidebarCompact}>
                <MultiLevelMenu sidebarCompact={false} setShowMobileSideBar={setShowMobileSideBar} />
            </NavWrapper>
        </LayoutDrawer>
    );
};

export default MobileSidebar;
